import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { GlobalService } from "@bitwarden/common/services/global/global.service";

import { ToggleSwitchComponent } from "@bitwarden/web-vault/app/components/toggle-switch/toggle-switch.component";
import { AUTO_REDEEM_CREDITS } from "@bitwarden/web-vault/app/models/types/auto-credit-redemption.types";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { AutoCreditRedemptionService } from "@bitwarden/web-vault/app/services/subscription/auto-credit-redemption.service";

/**
 * Shared "auto credit redemption" toggle, rendered on both the Bundle page and the Billing page.
 * Owns its own eligibility check, post-login sync/enrollment bootstrap, and toggle handling so
 * it behaves identically wherever it's placed.
 */
@Component({
  selector: "auto-credit-redemption-toggle",
  templateUrl: "./auto-credit-redemption-toggle.component.html",
})
export class AutoCreditRedemptionToggleComponent {
  private userStoreService: UserStoreService = inject(UserStoreService);
  private globalService: GlobalService = inject(GlobalService);
  private logService: LogService = inject(LogService);
  private autoCreditRedemptionService: AutoCreditRedemptionService = inject(
    AutoCreditRedemptionService,
  );

  /** Extra classes for the card, e.g. grid column spans on the host page. */
  readonly cardClass = input<string>("");

  /**
   * `card` = standalone card (Plans page); `row` = DS 2.1 row without card chrome, for use inside
   * another card (the Payment method card on the Subscription page).
   */
  readonly variant = input<"card" | "row">("card");

  protected readonly autoRedeemCredits = AUTO_REDEEM_CREDITS;
  protected isSyncing = signal(false);
  private toggleSwitch = viewChild(ToggleSwitchComponent);

  protected showToggle = computed(() =>
    this.autoCreditRedemptionService.canShowToggle(
      this.userStoreService.subscription.activeSubscription(),
    ),
  );

  protected isEnabled = computed(
    () => this.userStoreService.preferences.preferenceView()?.autoCreditRedemption ?? true,
  );

  private initStarted = false;

  constructor() {
    // Skip gloss-api when the feature flag is off - nobody can see the toggle then.
    effect(() => {
      if (this.initStarted || !this.autoCreditRedemptionService.isFeatureEnabled()) {
        return;
      }
      this.initStarted = true;
      untracked(() => void this.initAutoCreditRedemption());
    });
  }

  async onToggle(enabled: boolean): Promise<void> {
    const view = this.userStoreService.preferences.preferenceView();
    if (this.isSyncing() || !view) {
      this.resyncToggle();
      return;
    }
    this.isSyncing.set(true);
    const preference = view.clone();
    preference.autoCreditRedemption = enabled;
    try {
      await this.autoCreditRedemptionService.syncEnrollmentFromPreference(enabled, preference);
    } catch (error) {
      this.logService.error(
        `[auto-redeem toggle] enrollment failed (enabled=${enabled}): ${error instanceof Error ? error.message : String(error)}`,
      );
      this.resyncToggle();
      this.globalService.showErrorMessage("error", "autoCreditRedemptionEnrollFailed");
    } finally {
      this.isSyncing.set(false);
    }
  }

  /** toggle-switch flips its own state on click; show the store's value again. */
  private resyncToggle(): void {
    this.toggleSwitch()?.resync(this.isEnabled());
  }

  private async initAutoCreditRedemption(): Promise<void> {
    if (this.autoCreditRedemptionService.hasHandledPostLogin()) {
      return;
    }

    await this.autoCreditRedemptionService.syncActiveSubscriptionFromGlossApi();
    await this.autoCreditRedemptionService.ensureEnrollmentIfOptedIn();
  }
}
